import { useMemo } from 'react';
import { useEventStream, ActivityEventPayload, ConnectionState } from './useEventStream';

/**
 * Activity event - re-exported from useEventStream for convenience.
 */
export type { ActivityEventPayload as ActivityEvent } from './useEventStream';
export type { ConnectionState } from './useEventStream';

/**
 * Options for the useActivityStream hook.
 */
interface UseActivityStreamOptions {
    /** Subject type: 'contact' or 'customer_account' */
    subjectType?: 'contact' | 'customer_account';
    /** Subject identifier */
    subjectIdentifier?: string;
    /** Callback when a new activity is received */
    onActivity?: (activity: ActivityEventPayload) => void;
    /** Callback when connection state changes */
    onConnectionChange?: (state: ConnectionState) => void;
    /** Whether the stream should be active (default: true) */
    enabled?: boolean;
}

/**
 * Hook for subscribing to activity updates via Server-Sent Events.
 *
 * This is a convenience wrapper around useEventStream specifically for
 * activity events. For more control or to receive other event types,
 * use useEventStream directly.
 *
 * @example
 * ```tsx
 * // Subscribe to activities for a specific contact
 * useActivityStream({
 *     subjectType: 'contact',
 *     subjectIdentifier: 'con-abc123',
 *     onActivity: (activity) => {
 *         console.log('New activity:', activity);
 *         // Invalidate Relay cache or update local state
 *     }
 * });
 *
 * // Subscribe to all activities globally
 * useActivityStream({
 *     onActivity: (activity) => console.log('New activity:', activity)
 * });
 * ```
 */
export function useActivityStream(options: UseActivityStreamOptions = {}) {
    const {
        subjectType,
        subjectIdentifier,
        onActivity,
        onConnectionChange,
        enabled = true
    } = options;

    // Build channels based on options
    const channels = useMemo(() => {
        const channelList = ['activities'];

        if (subjectType && subjectIdentifier) {
            channelList.push(`${subjectType}:${subjectIdentifier}`);
        }

        return channelList;
    }, [subjectType, subjectIdentifier]);

    return useEventStream({
        channels,
        onActivity,
        onConnectionChange,
        enabled,
    });
}

export default useActivityStream;
